import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from '../../../store';
import { GeneralActions } from '../../../store/entities/general/general.actions';
import useTranslations from '../../../hooks/useTranslations';
import useGetGridData from '../../../hooks/useGetGridData';
import ActionButtons, {
  IMenuItem,
} from '../../../components/UI/ActionButtons/ActionButtons';
import Title from '../../../components/UI/Title/Title';
import { ISortItem } from '../../../models/table';
import {
  getDateTimeAndSeconds,
  sortDataByDate,
} from '../../../utils/formatters';
import { getDynamicListItemName } from '../../../utils/helpers';
import useUrlSearchParam from '../../../hooks/useUrlSearchParams';
import { FieldTypes, IFormFields } from '../../../models/form';
import Filters from '../../../components/Container/Filters/Filters';
import {
  IDynamicList,
  ILockState,
  ILockStatus,
} from '../../../store/entities/general/type';
import { IAutoCompleteItem } from '../../../models/autoComplete';
import TableButtons from '../../../components/UI/TableButtons/TableButtons';
import { LocksActions } from '../../../store/entities/locks/locks.actions';
import { ILock } from '../../../store/entities/locks/type';
import { lockListSearchValidation } from '../../../utils/formValidation/locksManagement';
import { IPage } from '../../../models/page';
import { TableActions } from '../../../store/entities/table/table.actions';
import { IButton } from '../../../models/button';
import { CONSTANTS } from '../../../utils/constants';
import { PERMISSIONS } from '../../../utils/permissions';
import EnhancedTable from '../../../components/UI/RcTable/EnhancedTable';
import { LockStatus } from '../../../models/locks';
import CSVLinkContainer from '../../../components/Container/CSVLinkContainer/CSVLinkContainer';

const ManageLocks = ({ permission, nextRoute }: IPage) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loclStatusList, setLoclStatusList] = useState<IAutoCompleteItem[]>([]);
  const [pemTypeList, setPemTypeList] = useState<IAutoCompleteItem[]>([]);

  const dispatch: any = useAppDispatch();
  const { rcTranslate } = useTranslations();
  const { onPageChange } = useGetGridData();
  const { getValueFromQuery, params } = useUrlSearchParam();
  const navigate = useNavigate();

  const { totalRows, queryParams, nextPageNumber, currentPage, sortModel } =
    useAppSelector((state) => state.table);
  const { locks } = useAppSelector((state) => state.locks);
  const { appLanguage, lockStatuses, pemTypes, doorStates, lockStates } = useAppSelector(
    (state) => state.general
  );
  const { appPermissions }: { appPermissions: string[] } = useAppSelector(
    (store) => store.auth
  );
  const { formikProps } = useAppSelector((store) => store.form);

  useEffect(() => {
    if (permission === false) {
      navigate(nextRoute);
    }
  }, [permission, nextRoute, navigate]);

  useEffect(() => {
    if (!permission) return;
    if (currentPage !== 1) {
      dispatch(TableActions.setTableData({ currentPage: 1 }));
    }
    dispatch(LocksActions.getLocks(params));
  }, [dispatch, params, permission]);

  useEffect(() => {
    if (nextPageNumber) {
      const getData = async () => {
        await dispatch(
          LocksActions.getLocks(undefined, sortModel, false, nextPageNumber)
        );
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [dispatch, nextPageNumber]);

  useEffect(() => {
    if (sortModel.length) {
      const getData = async () => {
        await dispatch(LocksActions.getLocks(undefined, sortModel, false));
        await dispatch(TableActions.setTableData({ nextPageNumber: null }));
      };
      getData();
    }
  }, [sortModel]);

  useEffect(() => {
    dispatch(GeneralActions.getLockStatuses());
    dispatch(GeneralActions.getDoorStates());
    dispatch(GeneralActions.getLockStates());
    dispatch(GeneralActions.getPemTypes());
  }, [dispatch]);

  const columns: any[] = [
    {
      id: 'caseId',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.caseId'),
    },
    {
      id: 'electronicUnitNumber',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.electronicUnitNumber'),
    },
    {
      id: 'macAddress',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.macAddress'),
    },
    {
      id: 'pemType',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.pemType'),
    },
    {
      id: 'firmwareVersion',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.firmwareVersion'),
    },
    {
      id: 'batteryLevel',
      numeric: true,
      disablePadding: true,
      label: rcTranslate('locks.filters.batteryLevel'),
    },
    {
      id: 'counter',
      numeric: true,
      disablePadding: true,
      label: rcTranslate('locks.filters.counter'),
    },
    {
      id: 'lastUserCounter',
      numeric: true,
      disablePadding: true,
      label: rcTranslate('locks.filters.lastUserCounter'),
    },
    {
      id: 'doorState',
      numeric: true,
      disablePadding: true,
      label: rcTranslate('locks.filters.doorState'),
    },
    {
      id: 'lockState',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.lockState'),
    },
    {
      id: 'pairingDate',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.lastDatePairing'),
    },
    {
      id: 'lockStatus',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.lockStatus'),
    },
    {
      id: 'statusUpdateDate',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.statusUpdateDate'),
    },
    {
      id: 'lastError',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.lastError'),
    },
    {
      id: 'isResetRequired',
      numeric: false,
      disablePadding: true,
      label: rcTranslate('locks.filters.isResetRequired'),
    },
  ];

  const navigateToAction = useCallback(
    (siteId: string, siteTypeId: number | undefined) => {
      // navigate(
      //     "/" +
      //     CONSTANTS.ROUTES.SITES_MANAGEMENT.BASE +
      //     "/" +
      //     CONSTANTS.ROUTES.SITES_MANAGEMENT.CHILDREN.SITE_DETAILS +
      //     "/" +
      //     siteId,
      //     {
      //         state: {
      //             edit: true,
      //             siteTypeId: siteTypeId ?? null
      //         }
      //     }
      // );
    },
    [navigate]
  );

  useEffect(() => {
    if (pemTypes?.length) {
      const newPemTypesList: IAutoCompleteItem[] = pemTypes.map((pem) => ({
        id: pem.id,
        label: appLanguage === 'hebrew' ? pem.nameHE : (pem.nameEN ?? ''),
      }));
      setPemTypeList(newPemTypesList);
    }
  }, [appLanguage, pemTypes]);

  useEffect(() => {
    if (lockStatuses.length) {
      const newLockStatuses: IAutoCompleteItem[] = lockStatuses.map(
        (status: ILockStatus) => ({
          id: status.id,
          label:
            appLanguage === 'hebrew' ? status.nameHE : (status.nameEN ?? ''),
        })
      );

      setLoclStatusList(newLockStatuses);
    }
  }, [appLanguage, lockStatuses]);

  const mapLocksToTableRows = useCallback(
    (data: ILock[]) => {
      return data.map((lock: ILock, index: number) => {
        const pemType = () => {
          if (!lock || !lock.pemType) return '';
          return getDynamicListItemName(pemTypes, lock.pemType, appLanguage);
        };

        const lockStatus = () => {
          if (!lock || !lock.lockStatus) return '';
          return getDynamicListItemName(lockStatuses, lock.lockStatus, appLanguage);
        };

        const doorState = () => {
          if (!lock ||!lock.doorState) return '';
          return getDynamicListItemName(doorStates, lock.doorState, appLanguage);
        };

        const lockStateValue = () => {
          if (!lock || !lock.lockState) return '';
          return getDynamicListItemName(lockStates, lock.lockState, appLanguage);
        };

        const menuItems: IMenuItem[] = [];

        const getLockStatusTransitions = () => {
          switch (lock.lockStatus) {
            case LockStatus.BLANK:
              return [LockStatus.FREE, LockStatus.DAMAGED];

            case LockStatus.DAMAGED:
              return [LockStatus.FREE];

            case LockStatus.FREE:
              return [LockStatus.DAMAGED];

            case LockStatus.PAIRED:
              return [];

            default:
              return [];
          }
        };

        getLockStatusTransitions().forEach((transition) =>
          menuItems.push({
            label: `${rcTranslate('locks.locksManagement.rowActions.changeStatusOfLockTo', [{ label: 'status', value: getDynamicListItemName(lockStatuses, transition, appLanguage) }])}`,
            onClick: async () => {
              await dispatch(
                LocksActions.changeLockStatus({
                  CaseId: lock?.caseId,
                  NewStatus: transition,
                })
              );
            },
            show: appPermissions.includes(PERMISSIONS.CHANGE_LOCK_STATUS),
          })
        );

        const siteObject = {
          id: index,
          caseId: lock?.caseId ?? '',
          electronicUnitNumber: lock?.electronicUnitNumber ?? '',
          macAddress: lock?.macAddress ?? '',
          pemType: pemType(),
          firmwareVersion: lock?.firmwareVersion ?? '',
          batteryLevel: lock?.batteryLevel ?? '',
          counter: lock?.counter ?? '',
          lastUserCounter: lock?.lastUserCounter ?? '',
          doorState: doorState(),
          lockState: lockStateValue(),
          pairingDate:
            lock?.pairingDate && getDateTimeAndSeconds(lock.pairingDate),
          lockStatus: lockStatus(),
          statusUpdateDate:
            lock?.statusUpdateDate &&
            getDateTimeAndSeconds(lock?.statusUpdateDate),
          lastError: lock?.lastError ?? '',
          isResetRequired: lock.isResetRequired
            ? rcTranslate('yes')
            : rcTranslate('no'),
          actions: (
            <ActionButtons
              style={{ marginRight: 'auto' }}
              hide={
                menuItems.every((v) => !v?.show) ||
                [PERMISSIONS.CHANGE_LOCK_STATUS].filter((v: string) =>
                  appPermissions.includes(v)
                ).length === 0
              }
              menuItems={menuItems}
            />
          ),
        };

        return siteObject;
      });
    },
    [
      appLanguage,
      appPermissions,
      dispatch,
      doorStates,
      lockStates,
      lockStatuses,
      pemTypes,
      rcTranslate,
    ]
  );

  useEffect(() => {
    if (!locks?.length) return setRows([]);

    const sitesRows = mapLocksToTableRows(locks);

    setRows(sitesRows);
  }, [locks, mapLocksToTableRows]);

  const searchFields: IFormFields = {
    initialValues: {
      caseId: getValueFromQuery('caseId'),
      macAddress: getValueFromQuery('macAddress'),
      lockStatus: getValueFromQuery('lockStatus', 'number'),
      pemType: getValueFromQuery('pemType', 'number'),
      electronicUnitNumber: getValueFromQuery('electronicUnitNumber'),
      firmwareVersion: getValueFromQuery('firmwareVersion'),
      dateRangeBy: getValueFromQuery('dateRangeBy'),
      fromDate: getValueFromQuery('fromDate'),
      toDate: getValueFromQuery('toDate'),
      isResetRequired: getValueFromQuery('isResetRequired'),
    },
    formData: [
      {
        type: FieldTypes.CHILDREN,
        id: 'externalFieldsWrapper',
        fields: [
          {
            type: FieldTypes.TEXT,
            id: 'caseId',
            label: 'locks.filters.caseId',
          },
          {
            type: FieldTypes.TEXT,
            id: 'macAddress',
            label: 'locks.filters.macAddress',
          },
          {
            type: FieldTypes.AUTOCOMPLETE,
            id: 'lockStatus',
            label: 'locks.filters.lockStatus',
            items: loclStatusList,
            props: { multiple: false },
          },
        ],
      },
      {
        type: FieldTypes.CHILDREN,
        id: 'extendedFieldsWrapper',
        fields: [
          {
            type: FieldTypes.CHILDREN,
            id: 'extendedFieldsContainer',
            fields: [
              {
                type: FieldTypes.CHILDREN,
                id: 'extendedFields',
                className: 'filters__extendedFields',
                fields: [
                  {
                    type: FieldTypes.AUTOCOMPLETE,
                    id: 'pemType',
                    label: 'locks.filters.pemType',
                    items: pemTypeList,
                    props: { multiple: false },
                  },
                  {
                    type: FieldTypes.TEXT,
                    id: 'electronicUnitNumber',
                    label: 'locks.filters.electronicUnitNumber',
                  },
                  {
                    type: FieldTypes.TEXT,
                    id: 'firmwareVersion',
                    label: 'locks.filters.firmwareVersion',
                  },
                  {
                    type: FieldTypes.SELECT,
                    id: 'dateRangeBy',
                    label: 'locks.filters.dateRangeBy.label',
                    items: [
                      {
                        key: '0',
                        value: 'locks.filters.dateRangeBy.pairingDate',
                      },
                      {
                        key: '1',
                        value: 'locks.filters.dateRangeBy.createdDate',
                      },
                      {
                        key: '2',
                        value: 'locks.filters.dateRangeBy.statusUpdateDate',
                      },
                    ],
                  },
                  {
                    type: FieldTypes.DATE,
                    id: 'fromDate',
                    label: 'locks.filters.fromDate',
                    props: { getStartOfDay: true },
                  },
                  {
                    type: FieldTypes.DATE,
                    id: 'toDate',
                    label: 'locks.filters.toDate',
                    props: {
                      getFullDayTime: true,
                      numOfDays: 1,
                      minDate: new Date(formikProps?.values?.fromDate),
                    },
                  },
                  {
                    type: FieldTypes.SELECT,
                    id: 'isResetRequired',
                    label: 'locks.filters.isResetRequired',
                    items: [
                      {
                        key: 'true',
                        value: rcTranslate('yes'),
                      },
                      {
                        key: 'false',
                        value: rcTranslate('no'),
                      },
                    ],
                    props: { multiple: false },
                  },
                ],
              },
              {
                type: FieldTypes.CHILDREN,
                id: 'actionsContainer',
                className: 'filters__extendedActions',
                fields: [
                  {
                    type: FieldTypes.BUTTON,
                    id: 'closeExtendedSearch',
                    label: 'searchFields.closeExtendedSearch',
                  },
                  {
                    type: FieldTypes.CHILDREN,
                    id: 'submitButtonWrapper',
                    className: 'filters__submitButtonWrapper',
                    fields: [
                      {
                        type: FieldTypes.SUBMIT_BUTTON,
                        id: 'submitButton',
                        label: '',
                      },
                      {
                        type: FieldTypes.BUTTON,
                        id: 'cleanSearch',
                        label: 'searchFields.cleanSearch',
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  };

  const generalButtons = [
    {
      title: 'locks.locksManagement.addLockButton',
      onClick: () => {
        navigate(
          '/' +
            CONSTANTS.ROUTES.LOCKS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.LOCKS_MANAGEMENT.CHILDREN.ADD_LOCK
        );
      },
      // show: appPermissions.includes(PERMISSIONS.RELEASE_MIGRATION)
    },
    {
      title: 'locks.locksManagement.releaseMigrationLockButton',
      onClick: () => {
        navigate(
          '/' +
            CONSTANTS.ROUTES.LOCKS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.LOCKS_MANAGEMENT.CHILDREN.RELEASE_MIGRATION_LOCK
        );
      },
      show: appPermissions.includes(PERMISSIONS.RELEASE_MIGRATION),
    },
  ];

  const tableButtons: IButton[] = [
    {
      title: 'locks.locksManagement.action',
      show: true,
      renderComponent: (
        <CSVLinkContainer
          columns={columns}
          getData={async () =>
            dispatch(LocksActions.getLocks(params, null, false, 1, totalRows))
          }
          mapData={mapLocksToTableRows}
          disabled={totalRows === 0}
          fileNamePrefix={'Locks_List'}
        />
      ),
    },
  ];

  const handleSortChange = (model: ISortItem[]) => {
    dispatch(LocksActions.getLocks(queryParams, model));
  };

  return (
    <>
      <Title text="locks.locksManagement.pageTitle" />

      <Filters
        fields={searchFields}
        filtersValidation={lockListSearchValidation}
      />

      <TableButtons
        generalButtons={generalButtons}
        tableButtons={tableButtons}
      />

      <div className="customersList">
        <EnhancedTable
          rows={rows}
          columns={columns}
          sortDataByDate={sortDataByDate}
          checkboxSelection={true}
          expandable={false}
          showId={false}
        />
      </div>
    </>
  );
};

export default ManageLocks;
